/**
 * Billing page.
 * Admin: Calculate billing and manage billing record lifecycle via transaction service.
 * Pilot: View own billing records (read-only) with account summary, monthly grouping,
 *        date range calculator, and CSV export.
 * Route: #/billing
 * Requirements: 5.1, 5.2, 5.3, 5.4, 5.5, 15.1, 15.2, 15.3, 15.4
 */

import { getUser, getRole, isAdmin } from '../services/auth.js';
import {
  getBillingRecords,
  getPilotFlyingHours,
  getBillingRecordLines,
  createBillingRecordLine,
  updateBillingRecordLine,
  deleteBillingRecordLine,
  updateFlight,
  getPaymentAllocations,
  createPaymentAllocation,
  getOutstandingInvoices,
  updateInvoice,
} from '../services/api.js';
import { calculateBilling, transitionBilling } from '../services/transactions.js';
import { navigate } from '../services/router.js';
import { formatGBP, formatDateUK, minsToHMM } from '../utils/format.js';
import { BILLING_STATUS } from '../utils/constants.js';
import { createDataGrid } from '../components/data-grid.js';
import { createCsvExportButton } from '../components/csv-export-button.js';
import { getSupabase } from '../services/supabase.js';
import { createBillingLedger } from '../components/billing-ledger.js';

// ---------------------------------------------------------------------------
// Pure helper functions (exported for testability)
// ---------------------------------------------------------------------------

/**
 * Compute account balance from pilot_account_entries.
 * Balance = sum(CREDIT amounts) − sum(DEBIT amounts).
 *
 * @param {Array<{ entry_type: 'CREDIT'|'DEBIT', amount: number }>} entries
 * @returns {number}
 */
export function computeBalance(entries) {
  if (!entries || entries.length === 0) return 0;
  let balance = 0;
  for (const e of entries) {
    const amt = Number(e.amount) || 0;
    if (e.entry_type === 'CREDIT') balance += amt;
    else if (e.entry_type === 'DEBIT') balance -= amt;
  }
  return balance;
}

/**
 * Group billing records by billing period (calendar month).
 * Each group key is "YYYY-MM" and contains the records for that month.
 * Groups are sorted descending by period key.
 *
 * @param {Object[]} records - billing records with period_start
 * @returns {Array<{ period: string, label: string, records: Object[] }>}
 */
export function groupRecordsByPeriod(records) {
  if (!records || records.length === 0) return [];

  const groups = new Map();
  for (const r of records) {
    const d = new Date(r.period_start);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    if (!groups.has(key)) {
      const label = d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
      groups.set(key, { period: key, label, records: [] });
    }
    groups.get(key).records.push(r);
  }

  return Array.from(groups.values()).sort((a, b) => b.period.localeCompare(a.period));
}

/**
 * Compute totals for a set of billing records within a period group.
 * Returns sum of credits, debits, and net total.
 *
 * @param {Object[]} records - billing records with amount_due
 * @param {Array<{ entry_type: string, amount: number, entry_date: string }>} [accountEntries]
 * @param {string} [periodStart] - YYYY-MM-DD
 * @param {string} [periodEnd] - YYYY-MM-DD
 * @returns {{ totalDue: number, credits: number, debits: number }}
 */
export function computePeriodTotals(records, accountEntries, periodStart, periodEnd) {
  const totalDue = (records || []).reduce((sum, r) => sum + (Number(r.amount_due) || 0), 0);

  let credits = 0;
  let debits = 0;

  if (accountEntries && periodStart && periodEnd) {
    for (const e of accountEntries) {
      const d = e.entry_date;
      if (d >= periodStart && d <= periodEnd) {
        const amt = Number(e.amount) || 0;
        if (e.entry_type === 'CREDIT') credits += amt;
        else if (e.entry_type === 'DEBIT') debits += amt;
      }
    }
  }

  return { totalDue, credits, debits };
}

/**
 * Format a billing record for display.
 *
 * @param {Object} record - A billing record
 * @returns {{ date: string, description: string, debit: string, credit: string, balance: string }}
 */
export function formatBillingRecord(record, runningBalance) {
  const period = `${formatDateUK(record.period_start)} – ${formatDateUK(record.period_end)}`;
  const aircraft = record.aircraft?.registration || 'N/A';
  const description = `${aircraft} — ${record.total_billable_mins} mins @ ${formatGBP(record.rate_applied || 0)}/hr`;
  const amount = Number(record.amount_due) || 0;

  return {
    date: formatDateUK(record.period_start),
    description,
    debit: formatGBP(amount),
    credit: '',
    balance: formatGBP(runningBalance),
  };
}

/**
 * Filter records by a date range (inclusive).
 *
 * @param {Object[]} records - billing records with period_start and period_end
 * @param {string} startDate - YYYY-MM-DD
 * @param {string} endDate - YYYY-MM-DD
 * @returns {Object[]}
 */
export function filterRecordsByDateRange(records, startDate, endDate) {
  if (!records) return [];
  if (!startDate && !endDate) return records;

  return records.filter(r => {
    const rStart = r.period_start;
    const rEnd = r.period_end;
    if (startDate && rEnd < startDate) return false;
    if (endDate && rStart > endDate) return false;
    return true;
  });
}

/**
 * Generate a CSV string from billing records and account entries.
 * Columns: Date, Description, Debit, Credit, Balance
 *
 * @param {Object[]} billingRecords - sorted billing records
 * @param {Array<{ entry_type: string, amount: number, description: string, entry_date: string }>} accountEntries
 * @returns {string} CSV content
 */
export function generateCSV(billingRecords, accountEntries) {
  const rows = [];
  rows.push('Date,Description,Debit,Credit,Balance');

  // Merge billing records and account entries into a single timeline
  const items = [];

  for (const r of (billingRecords || [])) {
    const amount = Number(r.amount_due) || 0;
    const aircraft = r.aircraft?.registration || 'N/A';
    items.push({
      date: r.period_start,
      description: `${aircraft} flying charges (${r.total_billable_mins} mins)`,
      debit: amount,
      credit: 0,
    });
  }

  for (const e of (accountEntries || [])) {
    const amt = Number(e.amount) || 0;
    items.push({
      date: e.entry_date,
      description: e.description || (e.entry_type === 'CREDIT' ? 'Credit' : 'Debit'),
      debit: e.entry_type === 'DEBIT' ? amt : 0,
      credit: e.entry_type === 'CREDIT' ? amt : 0,
    });
  }

  // Sort by date ascending
  items.sort((a, b) => (a.date || '').localeCompare(b.date || ''));

  let balance = 0;
  for (const item of items) {
    balance = balance + item.credit - item.debit;
    const escapedDesc = `"${(item.description || '').replace(/"/g, '""')}"`;
    rows.push(`${item.date},${escapedDesc},${item.debit.toFixed(2)},${item.credit.toFixed(2)},${balance.toFixed(2)}`);
  }

  return rows.join('\n');
}

/**
 * Aggregate billing records by pilot (admin view).
 * Exported for testability.
 */
export function aggregateBillingByPilot(records) {
  const byPilot = new Map();

  for (const r of records) {
    const pid = r.pilot_id;
    if (!byPilot.has(pid)) {
      byPilot.set(pid, {
        pilot_id: pid,
        pilot_name: r.pilots?.name || 'Unknown',
        total_billable_mins: 0,
        amount_due: 0,
      });
    }
    const entry = byPilot.get(pid);
    entry.total_billable_mins += r.total_billable_mins || 0;
    entry.amount_due += r.amount_due || 0;
  }

  const pilotRows = Array.from(byPilot.values());
  const grandTotal = {
    total_billable_mins: pilotRows.reduce((s, p) => s + p.total_billable_mins, 0),
    amount_due: pilotRows.reduce((s, p) => s + p.amount_due, 0),
  };

  return { pilotRows, grandTotal };
}

/**
 * Filter billing records to only those belonging to a specific pilot.
 * Exported for testability.
 */
export function filterBillingByPilot(records, userId) {
  return records.filter((r) => r.pilot_id === userId);
}

/**
 * Compute the remaining balance on an invoice.
 * remaining = totalAmount − paidAmount, clamped to 0.
 *
 * @param {number} totalAmount - The invoice total_amount (amount_due)
 * @param {number} paidAmount  - The amount already paid
 * @returns {number} Remaining balance (≥ 0)
 */
export function computeRemainingBalance(totalAmount, paidAmount) {
  const total = Number(totalAmount) || 0;
  const paid = Number(paidAmount) || 0;
  const remaining = total - paid;
  return Math.max(0, Math.round(remaining * 100) / 100);
}

/**
 * Validate a payment allocation amount.
 * Returns an object with `valid` and optional `error` message.
 *
 * Rules:
 * - amount must be > 0
 * - amount must not exceed the invoice's remaining balance
 * - amount must not cause total allocated to exceed the credit amount
 *
 * @param {number} amount           - The allocation amount to validate
 * @param {number} remainingBalance - The invoice's remaining balance
 * @param {number} creditAmount     - The total credit entry amount
 * @param {number} totalAllocated   - Sum of all other allocations for this credit entry
 * @returns {{ valid: boolean, error?: string }}
 */
export function validateAllocationAmount(amount, remainingBalance, creditAmount, totalAllocated) {
  const amt = Number(amount);
  const remaining = Number(remainingBalance) || 0;
  const credit = Number(creditAmount) || 0;
  const allocated = Number(totalAllocated) || 0;

  if (isNaN(amt) || amt <= 0) {
    return { valid: false, error: 'Allocation amount must be greater than zero.' };
  }
  if (amt > remaining) {
    return { valid: false, error: `Allocation amount (${amt.toFixed(2)}) exceeds invoice remaining balance (${remaining.toFixed(2)}).` };
  }
  const availableCredit = Math.round((credit - allocated) * 100) / 100;
  if (amt > availableCredit) {
    return { valid: false, error: `Allocation amount (${amt.toFixed(2)}) exceeds available credit (${availableCredit.toFixed(2)}).` };
  }
  return { valid: true };
}

/**
 * Aggregate billing records into monthly statements per pilot.
 * Each output row is one (pilot, YYYY-MM) invoice with summed totals.
 *
 * @param {Object[]} records - Billing records (legacy shape) with pilot_id, period_start, amount_due
 * @returns {Array<{
 *   key: string, pilot_id: string, pilot_name: string, pilot_role: string, is_owner: boolean,
 *   month: string, month_label: string, flight_count: number,
 *   total_mins: number, total_amount: number, status: string
 * }>}
 */
export function aggregateInvoicesByPilotMonth(records) {
  const groups = new Map();

  for (const r of (records || [])) {
    if (!r.pilot_id || !r.period_start) continue;
    const d = new Date(r.period_start);
    const month = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const key = `${r.pilot_id}|${month}`;

    if (!groups.has(key)) {
      groups.set(key, {
        key,
        pilot_id:    r.pilot_id,
        pilot_name:  r.pilots?.name || 'Unknown',
        pilot_role:  r.pilots?.role || 'pilot',
        is_owner:    (r.pilots?.role === 'owner'),
        month,
        month_label: d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }),
        flight_count: 0,
        total_mins:   0,
        total_amount: 0,
        status:      'DRAFT',  // All Path-A invoices are DRAFT; Path B will persist + promote to ISSUED/PAID.
      });
    }

    const g = groups.get(key);
    g.flight_count += 1;
    g.total_mins   += Number(r.total_billable_mins) || 0;
    g.total_amount += Number(r.amount_due) || 0;
  }

  return Array.from(groups.values());
}

// ---------------------------------------------------------------------------
// Page rendering
// ---------------------------------------------------------------------------

export async function renderBilling() {
  const app = document.getElementById('app');
  const user = getUser();

  if (!user) { navigate('/login'); return; }


  if (!isAdmin()) {
    await renderPilotBilling(app, user);
    return;
  }

  app.innerHTML = `
    <div class="container">
      <h2>Billing Administration</h2>
      <div id="billing-msg"></div>

      <div class="card">
        <h3>Calculate Billing</h3>
        <form id="calc-billing-form" style="display:flex;gap:1rem;flex-wrap:wrap;align-items:end;">
          <div class="form-group" style="margin-bottom:0;">
            <label for="billing-start">Period Start</label>
            <input type="date" id="billing-start" required />
          </div>
          <div class="form-group" style="margin-bottom:0;">
            <label for="billing-end">Period End</label>
            <input type="date" id="billing-end" required />
          </div>
          <button type="submit" class="btn btn--primary">Calculate</button>
        </form>
      </div>

      <div id="billing-warnings" style="display:none;" class="card"></div>
      <div id="billing-results" style="display:none;" class="card"></div>

      <div class="card">
        <h3>Cost Summary</h3>
        <div id="cost-summary">Select a billing period and calculate to view cost summary.</div>
      </div>

      <div class="card">
        <div class="billing-tabs" role="tablist" style="display:flex;gap:0.5rem;border-bottom:1px solid var(--color-border, #ddd);margin-bottom:1rem;">
          <button type="button" class="billing-tab btn btn--secondary" data-tab="invoices" role="tab" aria-selected="true"
                  style="border-radius:4px 4px 0 0;border-bottom:2px solid var(--color-primary, #06f);">Invoices</button>
          <button type="button" class="billing-tab btn btn--secondary" data-tab="flights" role="tab" aria-selected="false"
                  style="border-radius:4px 4px 0 0;">Flights</button>
        </div>

        <section id="tab-invoices" class="billing-tab-panel" role="tabpanel">
          <p style="margin:0 0 0.75rem 0;color:var(--color-text-muted);font-size:0.9rem;">
            Monthly statements — one row per (pilot, month). Owner-flown rows are greyed out.
            Nothing persisted yet; use "Issue Invoices" (coming next session) to crystallise.
          </p>
          <div id="invoice-filters" style="display:flex;gap:0.75rem;flex-wrap:wrap;align-items:end;margin-bottom:0.75rem;">
            <div class="form-group" style="margin-bottom:0;">
              <label for="inv-filter-pilot">Pilot</label>
              <select id="inv-filter-pilot"><option value="">All pilots</option></select>
            </div>
            <div class="form-group" style="margin-bottom:0;">
              <label for="inv-filter-month-from">Month From</label>
              <input type="month" id="inv-filter-month-from" />
            </div>
            <div class="form-group" style="margin-bottom:0;">
              <label for="inv-filter-month-to">Month To</label>
              <input type="month" id="inv-filter-month-to" />
            </div>
            <label style="display:flex;align-items:center;gap:0.25rem;margin-bottom:0;font-size:0.9rem;">
              <input type="checkbox" id="inv-filter-hide-owners" />
              Hide owner rows
            </label>
            <button type="button" id="inv-filter-clear" class="btn btn--secondary">Clear</button>
          </div>
          <div id="invoice-list">Loading…</div>
        </section>

        <section id="tab-flights" class="billing-tab-panel" role="tabpanel" hidden>
          <p style="margin:0 0 0.75rem 0;color:var(--color-text-muted);font-size:0.9rem;">
            Per-flight billing detail — every billed flight as a row.
          </p>
          <div id="billing-filters" style="display:flex;gap:0.75rem;flex-wrap:wrap;align-items:end;margin-bottom:0.75rem;">
            <div class="form-group" style="margin-bottom:0;">
              <label for="filter-pilot">Pilot</label>
              <select id="filter-pilot"><option value="">All pilots</option></select>
            </div>
            <div class="form-group" style="margin-bottom:0;">
              <label for="filter-aircraft">Aircraft</label>
              <select id="filter-aircraft"><option value="">All aircraft</option></select>
            </div>
            <div class="form-group" style="margin-bottom:0;">
              <label for="filter-status">Status</label>
              <select id="filter-status"><option value="">All statuses</option></select>
            </div>
            <button type="button" id="filter-clear" class="btn btn--secondary">Clear</button>
          </div>
          <div id="billing-list">Loading…</div>
        </section>
      </div>
    </div>
  `;

  await loadBillingRecords();

  // Tab switching
  const tabs   = document.querySelectorAll('.billing-tab');
  const panels = document.querySelectorAll('.billing-tab-panel');
  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      const target = tab.dataset.tab;
      tabs.forEach((t) => {
        const active = t.dataset.tab === target;
        t.setAttribute('aria-selected', active ? 'true' : 'false');
        t.style.borderBottom = active ? '2px solid var(--color-primary, #06f)' : 'none';
      });
      panels.forEach((p) => {
        p.hidden = (p.id !== `tab-${target}`);
      });
    });
  });

  // Calculate billing form
  document.getElementById('calc-billing-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const btn = e.target.querySelector('button[type="submit"]');
    btn.disabled = true;
    btn.textContent = 'Calculating…';

    const start = document.getElementById('billing-start').value;
    const end = document.getElementById('billing-end').value;
    const result = await calculateBilling(start, end);

    btn.disabled = false;
    btn.textContent = 'Calculate';

    if (result.error) {
      showMsg(result.error, true);
      return;
    }

    const warningsEl = document.getElementById('billing-warnings');
    const warnings = result.warnings || [];
    if (warnings.length) {
      warningsEl.style.display = 'block';
      warningsEl.innerHTML = '<h4 style="color:var(--color-warning);">Warnings</h4><ul>' +
        warnings.map(w => `<li>${typeof w === 'string' ? w : JSON.stringify(w)}</li>`).join('') + '</ul>';
    } else {
      warningsEl.style.display = 'none';
    }

    const records = result.records || [];
    const resultsEl = document.getElementById('billing-results');
    resultsEl.style.display = 'block';
    resultsEl.innerHTML = `<h4>Calculation Complete</h4><p>${records.length} billing record(s) created as DRAFT.</p>`;

    showMsg('Billing calculated successfully!', false);
    await loadBillingRecords();
    renderCostSummary(start, end);
  });
}

/**
 * Render the pilot billing view.
 * Shows account summary, billing records grouped by month, date range calculator,
 * and CSV export.
 * Requirements: 15.1, 15.2, 15.3, 15.4
 */
async function renderPilotBilling(app, user) {
  app.innerHTML = `
    <div class="container">
      <h2>My Billing</h2>
      <div id="billing-msg"></div>

      <div class="card">
        <h3>Date Range Calculator</h3>
        <form id="date-range-form" style="display:flex;gap:1rem;flex-wrap:wrap;align-items:end;">
          <div class="form-group" style="margin-bottom:0;">
            <label for="range-start">From</label>
            <input type="date" id="range-start" />
          </div>
          <div class="form-group" style="margin-bottom:0;">
            <label for="range-end">To</label>
            <input type="date" id="range-end" />
          </div>
          <button type="submit" class="btn btn--primary">Calculate</button>
          <button type="button" id="clear-range" class="btn btn--secondary">Clear</button>
        </form>
        <div id="range-result" style="margin-top:0.75rem;"></div>
      </div>

      <div class="card">
        <div style="display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:0.5rem;margin-bottom:0.75rem;">
          <h3 style="margin:0;">Billing Records</h3>
          <div id="csv-export-container"></div>
        </div>
        <div id="pilot-billing-records">Loading…</div>
      </div>
    </div>
  `;

  let allBillingRecords = [];
  let allAccountEntries = [];

  try {
    // Fetch account entries for balance
    const supabase = getSupabase();
    const { data: entries, error: accErr } = await supabase
      .from('pilot_account_entries')
      .select('entry_type, amount, description, entry_date')
      .eq('pilot_id', user.id)
      .order('entry_date', { ascending: false });
    if (accErr) throw accErr;
    allAccountEntries = entries || [];

    // Fetch billing records for this pilot
    const records = await getBillingRecords();
    allBillingRecords = filterBillingByPilot(records, user.id);
  } catch (err) {
    showMsg(`Error loading billing data: ${err.message}`, true);
  }

  // Mount billing ledger component (includes balance header + transaction table)
  mountBillingLedger(allBillingRecords, allAccountEntries);

  // Wire CSV export button
  const csvContainer = document.getElementById('csv-export-container');
  if (csvContainer) {
    const csvBtn = createCsvExportButton({
      label: '📥 Export CSV',
      filename: 'billing-records.csv',
      getCsvContent: () => generateCSV(allBillingRecords, allAccountEntries),
    });
    csvContainer.appendChild(csvBtn);
  }

  // Date range calculator
  const rangeForm = document.getElementById('date-range-form');
  if (rangeForm) {
    rangeForm.addEventListener('submit', (e) => {
      e.preventDefault();
      const startDate = document.getElementById('range-start').value;
      const endDate = document.getElementById('range-end').value;
      const filtered = filterRecordsByDateRange(allBillingRecords, startDate, endDate);
      const total = filtered.reduce((sum, r) => sum + (Number(r.amount_due) || 0), 0);

      const resultEl = document.getElementById('range-result');
      if (resultEl) {
        resultEl.innerHTML = `
          <p><strong>Records in range:</strong> ${filtered.length}</p>
          <p><strong>Total amount due:</strong> ${formatGBP(total)}</p>
        `;
      }

      // Re-render ledger with filtered data
      mountBillingLedger(filtered, allAccountEntries);
    });

    document.getElementById('clear-range').addEventListener('click', () => {
      document.getElementById('range-start').value = '';
      document.getElementById('range-end').value = '';
      document.getElementById('range-result').innerHTML = '';
      mountBillingLedger(allBillingRecords, allAccountEntries);
    });
  }
}

/**
 * Mount the billing ledger component into the pilot-billing-records container.
 * The ledger component renders the balance header and transaction table.
 *
 * @param {Object[]} billingRecords - billing records (debits) for this pilot
 * @param {Object[]} accountEntries - pilot_account_entries (credits) for this pilot
 */
function mountBillingLedger(billingRecords, accountEntries) {
  const container = document.getElementById('pilot-billing-records');
  if (!container) return;

  // Clear existing content
  while (container.firstChild) container.removeChild(container.firstChild);

  const ledger = createBillingLedger({
    debits: billingRecords,
    credits: accountEntries,
    adminMode: false,
  });
  container.appendChild(ledger);
}

/**
 * Render account summary showing balance.
 */
function renderAccountSummary(accountEntries) {
  const el = document.getElementById('account-summary');
  if (!el) return;

  const balance = computeBalance(accountEntries);
  const isCredit = balance >= 0;
  const balanceClass = isCredit ? 'color:var(--color-success)' : 'color:var(--color-danger)';
  const balanceLabel = isCredit ? 'Credit' : 'Debit';

  el.innerHTML = `
    <p style="font-size:1.25rem;">
      <strong>Account Balance:</strong>
      <span style="${balanceClass};font-weight:bold;">
        ${formatGBP(Math.abs(balance))} (${balanceLabel})
      </span>
    </p>
  `;
}

/**
 * Render billing records grouped by monthly billing period.
 */
function renderGroupedBillingRecords(billingRecords, accountEntries) {
  const container = document.getElementById('pilot-billing-records');
  if (!container) return;

  if (!billingRecords || billingRecords.length === 0) {
    container.innerHTML = '<p style="color:var(--color-text-muted);">No billing records found.</p>';
    return;
  }

  const groups = groupRecordsByPeriod(billingRecords);
  container.innerHTML = '';

  for (const group of groups) {
    const section = document.createElement('div');
    section.className = 'billing-period-group';
    section.style.marginBottom = '1.5rem';

    const header = document.createElement('h4');
    header.textContent = group.label;
    header.style.marginBottom = '0.5rem';
    section.appendChild(header);

    const totals = computePeriodTotals(
      group.records,
      accountEntries,
      group.records[0]?.period_start,
      group.records[group.records.length - 1]?.period_end
    );

    const summaryEl = document.createElement('div');
    summaryEl.style.cssText = 'display:flex;gap:1.5rem;margin-bottom:0.5rem;font-size:0.9rem;color:var(--color-text-muted);';
    summaryEl.innerHTML = `
      <span>Charges: ${formatGBP(totals.totalDue)}</span>
      <span style="color:var(--color-success);">Credits: ${formatGBP(totals.credits)}</span>
      <span style="color:var(--color-danger);">Debits: ${formatGBP(totals.debits)}</span>
    `;
    section.appendChild(summaryEl);

    const grid = createDataGrid({
      columns: [
        {
          key: 'period',
          label: 'Period',
          format: (_val, row) =>
            `${formatDateUK(row.period_start)} – ${formatDateUK(row.period_end)}`,
        },
        {
          key: 'aircraft',
          label: 'Aircraft',
          html: true,
          format: (_val, row) => {
            const reg = row.aircraft?.registration;
            const id = row.aircraft?.id || row.aircraft_id;
            if (!reg) return 'N/A';
            if (!id) return reg;
            return `<a href="#/aircraft/${id}" class="aircraft-link">${reg}</a>`;
          },
        },
        { key: 'total_billable_mins', label: 'Billable Mins' },
        {
          key: 'rate_applied',
          label: 'Rate',
          format: (val) => val != null ? formatGBP(val) : '—',
        },
        {
          key: 'amount_due',
          label: 'Amount Due',
          format: (val) => formatGBP(val),
        },
        { key: 'status', label: 'Status' },
      ],
      data: group.records,
      defaultSort: 'period',
      defaultOrder: 'desc',
      emptyMessage: 'No records for this period.',
      filterable: false,
    });

    section.appendChild(grid);
    container.appendChild(section);
  }
}

/**
 * Render the admin Cost Summary section as a Data_Grid.
 */
async function renderCostSummary(periodStart, periodEnd) {
  const container = document.getElementById('cost-summary');
  if (!container) return;

  container.textContent = 'Loading cost summary…';

  try {
    const allRecords = await getBillingRecords();
    const periodRecords = allRecords.filter(r => {
      return r.period_start >= periodStart && r.period_end <= periodEnd;
    });

    if (periodRecords.length === 0) {
      container.textContent = '';
      const emptyMsg = document.createElement('p');
      emptyMsg.style.color = 'var(--color-text-muted)';
      emptyMsg.textContent = 'No costs to reclaim for this period.';
      container.appendChild(emptyMsg);
      return;
    }

    const { pilotRows, grandTotal } = aggregateBillingByPilot(periodRecords);

    const gridData = [
      ...pilotRows,
      {
        pilot_id: '__grand_total__',
        pilot_name: 'Grand Total',
        total_billable_mins: grandTotal.total_billable_mins,
        amount_due: grandTotal.amount_due,
      },
    ];

    const grid = createDataGrid({
      columns: [
        { key: 'pilot_name', label: 'Pilot Name' },
        { key: 'total_billable_mins', label: 'Total Billable Minutes' },
        {
          key: 'amount_due',
          label: 'Total Amount Due',
          format: (val) => formatGBP(val),
        },
      ],
      data: gridData,
      defaultSort: 'pilot_name',
      defaultOrder: 'asc',
      emptyMessage: 'No costs to reclaim for this period.',
      filterable: true,
    });

    container.textContent = '';
    container.appendChild(grid);
  } catch (err) {
    container.textContent = '';
    const errMsg = document.createElement('p');
    errMsg.style.color = 'var(--color-danger)';
    errMsg.textContent = `Error loading cost summary: ${err.message}`;
    container.appendChild(errMsg);
  }
}

// Module-level cache so the filter dropdowns can re-render without re-fetching.
let _allBillingRecords = [];
let _allInvoices = [];

function applyInvoiceFilters(invoices) {
  const pilot     = document.getElementById('inv-filter-pilot')?.value || '';
  const monthFrom = document.getElementById('inv-filter-month-from')?.value || '';
  const monthTo   = document.getElementById('inv-filter-month-to')?.value || '';
  const hideOwn   = document.getElementById('inv-filter-hide-owners')?.checked || false;
  return invoices.filter((inv) => {
    if (pilot     && inv.pilot_id !== pilot) return false;
    if (monthFrom && inv.month < monthFrom)  return false;
    if (monthTo   && inv.month > monthTo)    return false;
    if (hideOwn   && inv.is_owner)           return false;
    return true;
  });
}

function populateInvoiceFilterDropdowns(invoices) {
  const pilotSel = document.getElementById('inv-filter-pilot');
  if (!pilotSel) return;
  const pilotsMap = new Map();
  for (const inv of invoices) pilotsMap.set(inv.pilot_id, inv.pilot_name);
  fillSelect(pilotSel, 'All pilots',
    [...pilotsMap.entries()].sort((a, b) => a[1].localeCompare(b[1])));
}

function renderInvoicesGrid() {
  const listEl = document.getElementById('invoice-list');
  if (!listEl) return;

  const filtered = applyInvoiceFilters(_allInvoices);
  while (listEl.firstChild) listEl.removeChild(listEl.firstChild);

  if (filtered.length === 0) {
    const empty = document.createElement('p');
    empty.style.color = 'var(--color-text-muted)';
    empty.textContent = 'No invoices match the current filters.';
    listEl.appendChild(empty);
    return;
  }

  // Summary: total for visible rows, excluding owners from the "billable" total
  const billableRows = filtered.filter((inv) => !inv.is_owner);
  const billableTotal = billableRows.reduce((s, inv) => s + inv.total_amount, 0);
  const billableHours = billableRows.reduce((s, inv) => s + (inv.total_mins / 60), 0);
  const ownerRows = filtered.filter((inv) => inv.is_owner);
  const ownerTotal = ownerRows.reduce((s, inv) => s + inv.total_amount, 0);

  const summary = document.createElement('p');
  summary.style.cssText = 'margin:0 0 0.5rem 0;color:var(--color-text-muted);font-size:0.9rem;';
  const ownerNote = ownerRows.length
    ? ` · ${ownerRows.length} owner row(s) worth ${formatGBP(ownerTotal)} shown greyed`
    : '';
  summary.textContent = `${filtered.length} invoices · ${billableRows.length} billable (${billableHours.toFixed(1)} hrs, ${formatGBP(billableTotal)})${ownerNote}`;
  listEl.appendChild(summary);

  const grid = createDataGrid({
    columns: [
      { key: 'month',        label: 'Month',
        format: (_v, r) => r.month_label },
      { key: 'pilot_name',   label: 'Pilot',
        html: true,
        format: (_v, r) => {
          const nameEsc = String(r.pilot_name).replace(/&/g, '&amp;').replace(/</g, '&lt;');
          return r.is_owner
            ? `<span style="color:var(--color-text-muted);font-style:italic;">${nameEsc} (owner)</span>`
            : nameEsc;
        },
      },
      { key: 'flight_count', label: 'Flights' },
      { key: 'total_mins',   label: 'Hours',
        format: (v) => (v / 60).toFixed(1) },
      { key: 'total_amount', label: 'Amount',
        format: (v) => formatGBP(v) },
      { key: 'status',       label: 'Status',
        html: true,
        format: (v) => `<span class="badge badge--draft">${v}</span>` },
    ],
    data: filtered,
    defaultSort: 'month',
    defaultOrder: 'desc',
    emptyMessage: 'No invoices.',
    filterable: true,
  });

  listEl.appendChild(grid);
}

function wireInvoiceFilters() {
  ['inv-filter-pilot', 'inv-filter-month-from', 'inv-filter-month-to'].forEach((id) => {
    const el = document.getElementById(id);
    if (el) el.onchange = renderInvoicesGrid;
  });
  const hideBox = document.getElementById('inv-filter-hide-owners');
  if (hideBox) hideBox.onchange = renderInvoicesGrid;
  const clearBtn = document.getElementById('inv-filter-clear');
  if (clearBtn) clearBtn.onclick = () => {
    ['inv-filter-pilot', 'inv-filter-month-from', 'inv-filter-month-to'].forEach((id) => {
      const el = document.getElementById(id);
      if (el) el.value = '';
    });
    const hb = document.getElementById('inv-filter-hide-owners');
    if (hb) hb.checked = false;
    renderInvoicesGrid();
  };
}

function applyAdminFilters(records) {
  const pilot    = document.getElementById('filter-pilot')?.value    || '';
  const aircraft = document.getElementById('filter-aircraft')?.value || '';
  const status   = document.getElementById('filter-status')?.value   || '';
  return records.filter((r) => {
    if (pilot    && r.pilot_id    !== pilot)    return false;
    if (aircraft && r.aircraft_id !== aircraft) return false;
    if (status   && r.status      !== status)   return false;
    return true;
  });
}

/**
 * Populate a <select> with options. Uses DOM methods (textContent, not innerHTML)
 * so user-supplied names cannot inject markup.
 */
function fillSelect(selectEl, allLabel, options) {
  while (selectEl.firstChild) selectEl.removeChild(selectEl.firstChild);
  const allOpt = document.createElement('option');
  allOpt.value = '';
  allOpt.textContent = allLabel;
  selectEl.appendChild(allOpt);
  for (const [value, label] of options) {
    const opt = document.createElement('option');
    opt.value = value;
    opt.textContent = label;
    selectEl.appendChild(opt);
  }
}

function populateAdminFilterDropdowns(records) {
  const pilotSel    = document.getElementById('filter-pilot');
  const aircraftSel = document.getElementById('filter-aircraft');
  const statusSel   = document.getElementById('filter-status');
  if (!pilotSel || !aircraftSel || !statusSel) return;

  const pilotsMap   = new Map();
  const aircraftMap = new Map();
  const statusSet   = new Set();
  for (const r of records) {
    if (r.pilot_id    && r.pilots?.name)            pilotsMap.set(r.pilot_id, r.pilots.name);
    if (r.aircraft_id && r.aircraft?.registration)  aircraftMap.set(r.aircraft_id, r.aircraft.registration);
    if (r.status)                                    statusSet.add(r.status);
  }

  fillSelect(pilotSel,    'All pilots',
    [...pilotsMap.entries()].sort((a, b) => a[1].localeCompare(b[1])));
  fillSelect(aircraftSel, 'All aircraft',
    [...aircraftMap.entries()].sort((a, b) => a[1].localeCompare(b[1])));
  fillSelect(statusSel,   'All statuses',
    [...statusSet].sort().map((s) => [s, s]));
}

function renderAdminBillingGrid() {
  const listEl = document.getElementById('billing-list');
  if (!listEl) return;

  const filtered = applyAdminFilters(_allBillingRecords);
  while (listEl.firstChild) listEl.removeChild(listEl.firstChild);

  if (filtered.length === 0) {
    const empty = document.createElement('p');
    empty.style.color = 'var(--color-text-muted)';
    empty.textContent = 'No records match the current filters.';
    listEl.appendChild(empty);
    return;
  }

  // Summary line — built from numeric aggregates only, no user input.
  const totalDue  = filtered.reduce((s, r) => s + (Number(r.amount_due) || 0), 0);
  const totalMins = filtered.reduce((s, r) => s + (Number(r.total_billable_mins) || 0), 0);
  const summary = document.createElement('p');
  summary.style.cssText = 'margin:0 0 0.5rem 0;color:var(--color-text-muted);font-size:0.9rem;';
  summary.textContent = `${filtered.length} records · ${(totalMins / 60).toFixed(1)} hours · ${formatGBP(totalDue)} total`;
  listEl.appendChild(summary);

  const grid = createDataGrid({
    columns: [
      {
        key: 'period_start',
        label: 'Date',
        format: (v) => formatDateUK(v),
      },
      {
        key: 'pilot_name',
        label: 'Pilot',
        format: (_v, r) => r.pilots?.name || 'Unknown',
      },
      {
        key: 'aircraft_reg',
        label: 'Aircraft',
        html: true,
        format: (_v, r) => {
          const reg = r.aircraft?.registration;
          const id  = r.aircraft_id;
          if (!reg) return 'N/A';
          if (!id)  return reg;
          return `<a href="#/aircraft/${id}" class="aircraft-link">${reg}</a>`;
        },
      },
      { key: 'total_billable_mins', label: 'Mins' },
      {
        key: 'rate_applied',
        label: 'Rate',
        format: (v) => v != null ? formatGBP(v) : '—',
      },
      {
        key: 'amount_due',
        label: 'Amount',
        format: (v) => formatGBP(v),
      },
      {
        key: 'status',
        label: 'Status',
        html: true,
        format: (v) => {
          const cls = v === 'PAID' ? 'badge--paid'
                    : v === 'INVOICED' ? 'badge--issued'
                    : v === 'UNBILLED' ? 'badge--draft'
                    : 'badge--draft';
          return `<span class="badge ${cls}">${v}</span>`;
        },
      },
    ],
    data: filtered,
    defaultSort: 'amount_due',
    defaultOrder: 'desc',
    emptyMessage: 'No billing records.',
    filterable: true,
    onRowClick: (row) => openInvoiceDetailModal(row),
  });

  listEl.appendChild(grid);
}

async function loadBillingRecords() {
  const listEl = document.getElementById('billing-list');
  if (!listEl) return;

  try {
    _allBillingRecords = await getBillingRecords();

    if (!_allBillingRecords.length) {
      while (listEl.firstChild) listEl.removeChild(listEl.firstChild);
      const empty = document.createElement('p');
      empty.style.color = 'var(--color-text-muted)';
      empty.textContent = 'No billing records.';
      listEl.appendChild(empty);
      return;
    }

    populateAdminFilterDropdowns(_allBillingRecords);
    renderAdminBillingGrid();

    // Monthly invoices — computed view on top of the same records
    _allInvoices = aggregateInvoicesByPilotMonth(_allBillingRecords);
    populateInvoiceFilterDropdowns(_allInvoices);
    renderInvoicesGrid();
    wireInvoiceFilters();

    // Re-render on filter change
    ['filter-pilot', 'filter-aircraft', 'filter-status'].forEach((id) => {
      const el = document.getElementById(id);
      if (el) el.onchange = renderAdminBillingGrid;
    });
    const clearBtn = document.getElementById('filter-clear');
    if (clearBtn) clearBtn.onclick = () => {
      ['filter-pilot', 'filter-aircraft', 'filter-status'].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.value = '';
      });
      renderAdminBillingGrid();
    };
  } catch (err) {
    while (listEl.firstChild) listEl.removeChild(listEl.firstChild);
    const errEl = document.createElement('p');
    errEl.style.color = 'var(--color-danger)';
    errEl.textContent = `Error loading records: ${err.message}`;
    listEl.appendChild(errEl);
  }
}

// ---------------------------------------------------------------------------
// Line type options for the add/edit form
// ---------------------------------------------------------------------------

const LINE_TYPE_OPTIONS = [
  { value: 'FLIGHT_COST', label: 'Flight Cost' },
  { value: 'FUEL', label: 'Fuel' },
  { value: 'OIL', label: 'Oil' },
  { value: 'FUEL_CARD', label: 'Fuel Card' },
  { value: 'LANDING_FEE', label: 'Landing Fee' },
];

/**
 * Return a human-readable label for a line type value.
 * Exported for testability.
 */
export function lineTypeLabel(value) {
  const opt = LINE_TYPE_OPTIONS.find((o) => o.value === value);
  return opt ? opt.label : value || '—';
}

/**
 * Determine whether an invoice is editable based on its status.
 * Editable when DRAFT, ISSUED, or UNBILLED. Not editable when PAID.
 * Exported for testability.
 */
export function isInvoiceEditable(status) {
  const s = (status || '').toUpperCase();
  return s !== 'PAID';
}

// ---------------------------------------------------------------------------
// Invoice Detail Modal — line-item display and editing (admin view)
// Requirements: 9.4, 9.5, 10.1, 10.2, 10.3, 10.4
// ---------------------------------------------------------------------------

/**
 * Open a modal showing invoice header info and line items.
 * Allows add/edit/remove of line items when invoice is DRAFT or ISSUED.
 * Recalculates amount_due as sum of line items on any change.
 * Prevents edits when invoice status is PAID.
 *
 * @param {Object} record - The billing record (legacy shape from getBillingRecords)
 */
async function openInvoiceDetailModal(record) {
  // Remove any existing modal
  const existing = document.getElementById('invoice-detail-modal');
  if (existing) existing.remove();

  const editable = isInvoiceEditable(record.status);

  // Create overlay
  const overlay = document.createElement('div');
  overlay.id = 'invoice-detail-modal';
  overlay.style.cssText = `
    position:fixed;top:0;left:0;width:100%;height:100%;
    background:rgba(0,0,0,0.5);z-index:1000;
    display:flex;align-items:center;justify-content:center;
  `;
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) closeModal();
  });

  const modal = document.createElement('div');
  modal.style.cssText = `
    background:var(--color-bg, #fff);border-radius:0.75rem;
    padding:1.5rem;max-width:700px;width:90%;max-height:85vh;
    overflow-y:auto;box-shadow:0 4px 24px rgba(0,0,0,0.2);
  `;

  // Header
  const header = document.createElement('div');
  header.style.cssText = 'display:flex;justify-content:space-between;align-items:center;margin-bottom:1rem;';

  const title = document.createElement('h3');
  title.style.margin = '0';
  title.textContent = 'Invoice Detail';

  const closeBtn = document.createElement('button');
  closeBtn.className = 'btn btn--secondary';
  closeBtn.textContent = '✕';
  closeBtn.style.cssText = 'font-size:1.25rem;padding:0.25rem 0.5rem;line-height:1;';
  closeBtn.addEventListener('click', closeModal);

  header.appendChild(title);
  header.appendChild(closeBtn);
  modal.appendChild(header);

  // Invoice info
  const info = document.createElement('div');
  info.style.cssText = 'margin-bottom:1rem;display:grid;grid-template-columns:1fr 1fr;gap:0.5rem;font-size:0.9rem;';
  const pilotName = record.pilots?.name || 'Unknown';
  const aircraftReg = record.aircraft?.registration || 'N/A';
  const statusBadge = record.status || 'DRAFT';

  info.innerHTML = `
    <div><strong>Date:</strong> ${formatDateUK(record.period_start)}</div>
    <div><strong>Pilot:</strong> ${escapeHtml(pilotName)}</div>
    <div><strong>Aircraft:</strong> ${escapeHtml(aircraftReg)}</div>
    <div><strong>Status:</strong> <span class="badge">${escapeHtml(statusBadge)}</span></div>
    <div><strong>Billable Mins:</strong> ${record.total_billable_mins || 0}</div>
    <div><strong>Rate:</strong> ${record.rate_applied != null ? formatGBP(record.rate_applied) : '—'}</div>
  `;
  modal.appendChild(info);

  // Amount due display
  const amountRow = document.createElement('div');
  amountRow.id = 'invoice-amount-due';
  amountRow.style.cssText = 'font-size:1.25rem;font-weight:bold;margin-bottom:1rem;';
  amountRow.textContent = `Amount Due: ${formatGBP(record.amount_due || 0)}`;
  modal.appendChild(amountRow);

  if (!editable) {
    const readOnlyNote = document.createElement('p');
    readOnlyNote.style.cssText = 'color:var(--color-text-muted);font-style:italic;margin-bottom:0.75rem;';
    readOnlyNote.textContent = 'This invoice is PAID and cannot be edited.';
    modal.appendChild(readOnlyNote);
  }

  // Status transition buttons (Requirements: 12.1, 12.2, 12.3, 12.4)
  const transitionSection = document.createElement('div');
  transitionSection.id = 'invoice-transition-section';
  transitionSection.style.cssText = 'margin-bottom:1rem;display:flex;gap:0.5rem;align-items:center;flex-wrap:wrap;';

  const transitionError = document.createElement('span');
  transitionError.id = 'invoice-transition-error';
  transitionError.style.cssText = 'color:var(--color-danger, red);font-size:0.9rem;display:none;';

  const currentStatus = (record.status || 'UNBILLED').toUpperCase();

  if (currentStatus === 'DRAFT' || currentStatus === 'UNBILLED') {
    const issueBtn = document.createElement('button');
    issueBtn.className = 'btn btn--primary';
    issueBtn.textContent = 'Issue Invoice';
    issueBtn.addEventListener('click', async () => {
      issueBtn.disabled = true;
      issueBtn.textContent = 'Issuing…';
      transitionError.style.display = 'none';
      const result = await transitionBilling(record.id, 'ISSUED');
      if (result.error) {
        transitionError.textContent = result.error;
        transitionError.style.display = 'inline';
        issueBtn.disabled = false;
        issueBtn.textContent = 'Issue Invoice';
      } else {
        record.status = 'ISSUED';
        // Re-open the modal to reflect the new status
        overlay.remove();
        await loadBillingRecords();
        await openInvoiceDetailModal(record);
      }
    });
    transitionSection.appendChild(issueBtn);
  } else if (currentStatus === 'ISSUED') {
    const paidBtn = document.createElement('button');
    paidBtn.className = 'btn btn--primary';
    paidBtn.textContent = 'Mark as Paid';
    paidBtn.addEventListener('click', async () => {
      paidBtn.disabled = true;
      paidBtn.textContent = 'Processing…';
      transitionError.style.display = 'none';
      const result = await transitionBilling(record.id, 'PAID');
      if (result.error) {
        transitionError.textContent = result.error;
        transitionError.style.display = 'inline';
        paidBtn.disabled = false;
        paidBtn.textContent = 'Mark as Paid';
      } else {
        record.status = 'PAID';
        // Re-open the modal to reflect the new status
        overlay.remove();
        await loadBillingRecords();
        await openInvoiceDetailModal(record);
      }
    });
    transitionSection.appendChild(paidBtn);
  }
  // PAID status: no transition buttons (invoice is locked)

  transitionSection.appendChild(transitionError);
  modal.appendChild(transitionSection);

  // Line items container
  const lineItemsContainer = document.createElement('div');
  lineItemsContainer.id = 'invoice-line-items';
  modal.appendChild(lineItemsContainer);

  // Add line item button (only if editable)
  if (editable) {
    const addBtn = document.createElement('button');
    addBtn.className = 'btn btn--primary';
    addBtn.textContent = '+ Add Line Item';
    addBtn.style.marginTop = '0.75rem';
    addBtn.addEventListener('click', () => openLineItemForm(null, record, refreshLineItems));
    modal.appendChild(addBtn);
  }

  // Payment allocations section
  const allocationsSection = document.createElement('div');
  allocationsSection.id = 'invoice-payment-allocations';
  allocationsSection.style.cssText = 'margin-top:1.5rem;border-top:1px solid var(--color-border, #ddd);padding-top:1rem;';
  modal.appendChild(allocationsSection);

  overlay.appendChild(modal);
  document.body.appendChild(overlay);

  // Load and render line items
  await refreshLineItems();

  async function refreshLineItems() {
    try {
      const lines = await getBillingRecordLines(record.id);
      renderLineItems(lineItemsContainer, lines, record, editable, refreshLineItems);

      // Recalculate and update amount display
      const total = lines.reduce((sum, l) => sum + (Number(l.amount) || 0), 0);
      const amountEl = document.getElementById('invoice-amount-due');
      if (amountEl) amountEl.textContent = `Amount Due: ${formatGBP(total)}`;

      // Load and render payment allocations
      await refreshPaymentAllocations();
    } catch (err) {
      lineItemsContainer.innerHTML = '';
      const errEl = document.createElement('p');
      errEl.style.color = 'var(--color-danger)';
      errEl.textContent = `Error loading line items: ${err.message}`;
      lineItemsContainer.appendChild(errEl);
    }
  }

  async function refreshPaymentAllocations() {
    const section = document.getElementById('invoice-payment-allocations');
    if (!section) return;

    section.innerHTML = '';

    const heading = document.createElement('h4');
    heading.style.margin = '0 0 0.75rem 0';
    heading.textContent = 'Payment Allocations';
    section.appendChild(heading);

    // If the record has an invoice_id, load allocations for that invoice
    const invoiceId = record.invoice_id;
    if (!invoiceId) {
      const noInvoice = document.createElement('p');
      noInvoice.style.color = 'var(--color-text-muted)';
      noInvoice.textContent = 'No invoice linked — payment allocations are managed at the invoice level.';
      section.appendChild(noInvoice);
      return;
    }

    try {
      const allocations = await getPaymentAllocations(invoiceId);
      renderPaymentAllocations(section, allocations, record);
    } catch (err) {
      const errEl = document.createElement('p');
      errEl.style.color = 'var(--color-danger)';
      errEl.textContent = `Error loading payment allocations: ${err.message}`;
      section.appendChild(errEl);
    }
  }

  function closeModal() {
    overlay.remove();
    // Refresh the billing grid to reflect any amount changes
    renderAdminBillingGrid();
  }
}

/**
 * Escape HTML to prevent XSS when inserting user-supplied text.
 */
function escapeHtml(str) {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

/**
 * Render line items table inside the given container.
 */
function renderLineItems(container, lines, record, editable, onRefresh) {
  container.innerHTML = '';

  if (!lines || lines.length === 0) {
    const empty = document.createElement('p');
    empty.style.color = 'var(--color-text-muted)';
    empty.textContent = 'No line items.';
    container.appendChild(empty);
    return;
  }

  const table = document.createElement('table');
  table.style.cssText = 'width:100%;border-collapse:collapse;';

  const thead = document.createElement('thead');
  let headerHtml = `
    <tr>
      <th style="text-align:left;padding:0.5rem;border-bottom:2px solid var(--color-border, #ddd);">Type</th>
      <th style="text-align:left;padding:0.5rem;border-bottom:2px solid var(--color-border, #ddd);">Description</th>
      <th style="text-align:right;padding:0.5rem;border-bottom:2px solid var(--color-border, #ddd);">Amount</th>
  `;
  if (editable) {
    headerHtml += `<th style="text-align:center;padding:0.5rem;border-bottom:2px solid var(--color-border, #ddd);">Actions</th>`;
  }
  headerHtml += '</tr>';
  thead.innerHTML = headerHtml;
  table.appendChild(thead);

  const tbody = document.createElement('tbody');
  for (const line of lines) {
    const tr = document.createElement('tr');
    tr.style.borderBottom = '1px solid var(--color-border, #eee)';

    const tdType = document.createElement('td');
    tdType.style.padding = '0.5rem';
    tdType.textContent = lineTypeLabel(line.line_type);
    tr.appendChild(tdType);

    const tdDesc = document.createElement('td');
    tdDesc.style.padding = '0.5rem';
    tdDesc.textContent = line.description || '';
    tr.appendChild(tdDesc);

    const tdAmount = document.createElement('td');
    tdAmount.style.cssText = 'text-align:right;padding:0.5rem;';
    tdAmount.textContent = formatGBP(Number(line.amount) || 0);
    tr.appendChild(tdAmount);

    if (editable) {
      const tdActions = document.createElement('td');
      tdActions.style.cssText = 'text-align:center;padding:0.5rem;';

      const editBtn = document.createElement('button');
      editBtn.className = 'btn btn--secondary';
      editBtn.textContent = 'Edit';
      editBtn.style.cssText = 'margin-right:0.25rem;font-size:0.8rem;padding:0.2rem 0.5rem;';
      editBtn.addEventListener('click', () => openLineItemForm(line, record, onRefresh));

      const deleteBtn = document.createElement('button');
      deleteBtn.className = 'btn btn--secondary';
      deleteBtn.textContent = 'Delete';
      deleteBtn.style.cssText = 'font-size:0.8rem;padding:0.2rem 0.5rem;color:var(--color-danger, red);';
      deleteBtn.addEventListener('click', async () => {
        if (!confirm('Delete this line item?')) return;
        try {
          await deleteBillingRecordLine(line.id);
          await recalcInvoiceTotal(record);
          await onRefresh();
        } catch (err) {
          alert(`Error deleting line item: ${err.message}`);
        }
      });

      tdActions.appendChild(editBtn);
      tdActions.appendChild(deleteBtn);
      tr.appendChild(tdActions);
    }

    tbody.appendChild(tr);
  }
  table.appendChild(tbody);

  // Total row
  const tfoot = document.createElement('tfoot');
  const totalAmount = lines.reduce((sum, l) => sum + (Number(l.amount) || 0), 0);
  let totalHtml = `
    <tr style="border-top:2px solid var(--color-border, #ddd);font-weight:bold;">
      <td style="padding:0.5rem;" colspan="2">Total</td>
      <td style="text-align:right;padding:0.5rem;">${formatGBP(totalAmount)}</td>
  `;
  if (editable) {
    totalHtml += '<td></td>';
  }
  totalHtml += '</tr>';
  tfoot.innerHTML = totalHtml;
  table.appendChild(tfoot);

  container.appendChild(table);
}

/**
 * Open an inline form for adding or editing a line item.
 * If `line` is null, it's an add operation; otherwise it's an edit.
 */
function openLineItemForm(line, record, onRefresh) {
  // Remove any existing form
  const existingForm = document.getElementById('line-item-form');
  if (existingForm) existingForm.remove();

  const isEdit = !!line;

  const form = document.createElement('div');
  form.id = 'line-item-form';
  form.style.cssText = `
    border:1px solid var(--color-border, #ddd);border-radius:0.5rem;
    padding:1rem;margin-top:0.75rem;background:var(--color-bg-alt, #f9f9f9);
  `;

  const formTitle = document.createElement('h4');
  formTitle.style.margin = '0 0 0.75rem 0';
  formTitle.textContent = isEdit ? 'Edit Line Item' : 'Add Line Item';
  form.appendChild(formTitle);

  // Type select
  const typeGroup = document.createElement('div');
  typeGroup.className = 'form-group';
  typeGroup.style.marginBottom = '0.5rem';
  const typeLabel = document.createElement('label');
  typeLabel.textContent = 'Type';
  typeLabel.setAttribute('for', 'line-item-type');
  const typeSelect = document.createElement('select');
  typeSelect.id = 'line-item-type';
  typeSelect.style.width = '100%';
  for (const opt of LINE_TYPE_OPTIONS) {
    const option = document.createElement('option');
    option.value = opt.value;
    option.textContent = opt.label;
    if (isEdit && line.line_type === opt.value) option.selected = true;
    typeSelect.appendChild(option);
  }
  typeGroup.appendChild(typeLabel);
  typeGroup.appendChild(typeSelect);
  form.appendChild(typeGroup);

  // Description input
  const descGroup = document.createElement('div');
  descGroup.className = 'form-group';
  descGroup.style.marginBottom = '0.5rem';
  const descLabel = document.createElement('label');
  descLabel.textContent = 'Description';
  descLabel.setAttribute('for', 'line-item-desc');
  const descInput = document.createElement('input');
  descInput.type = 'text';
  descInput.id = 'line-item-desc';
  descInput.style.width = '100%';
  descInput.value = isEdit ? (line.description || '') : '';
  descInput.placeholder = 'e.g. Fuel uplift 50L';
  descGroup.appendChild(descLabel);
  descGroup.appendChild(descInput);
  form.appendChild(descGroup);

  // Amount input
  const amountGroup = document.createElement('div');
  amountGroup.className = 'form-group';
  amountGroup.style.marginBottom = '0.75rem';
  const amountLabel = document.createElement('label');
  amountLabel.textContent = 'Amount (£)';
  amountLabel.setAttribute('for', 'line-item-amount');
  const amountInput = document.createElement('input');
  amountInput.type = 'number';
  amountInput.id = 'line-item-amount';
  amountInput.style.width = '100%';
  amountInput.step = '0.01';
  amountInput.min = '0';
  amountInput.value = isEdit ? (Number(line.amount) || 0).toFixed(2) : '';
  amountInput.placeholder = '0.00';
  amountGroup.appendChild(amountLabel);
  amountGroup.appendChild(amountInput);
  form.appendChild(amountGroup);

  // Error display
  const errorEl = document.createElement('p');
  errorEl.style.cssText = 'color:var(--color-danger, red);display:none;margin-bottom:0.5rem;';
  form.appendChild(errorEl);

  // Buttons
  const btnRow = document.createElement('div');
  btnRow.style.cssText = 'display:flex;gap:0.5rem;';

  const saveBtn = document.createElement('button');
  saveBtn.className = 'btn btn--primary';
  saveBtn.textContent = isEdit ? 'Save' : 'Add';
  saveBtn.addEventListener('click', async () => {
    const lineType = typeSelect.value;
    const description = descInput.value.trim();
    const amount = parseFloat(amountInput.value);

    if (!description) {
      errorEl.textContent = 'Description is required.';
      errorEl.style.display = 'block';
      return;
    }
    if (isNaN(amount) || amount < 0) {
      errorEl.textContent = 'Amount must be a valid non-negative number.';
      errorEl.style.display = 'block';
      return;
    }

    saveBtn.disabled = true;
    saveBtn.textContent = 'Saving…';

    try {
      if (isEdit) {
        await updateBillingRecordLine(line.id, {
          line_type: lineType,
          description,
          amount,
        });
      } else {
        await createBillingRecordLine({
          billing_record_id: record.id,
          line_type: lineType,
          description,
          amount,
        });
      }
      await recalcInvoiceTotal(record);
      form.remove();
      await onRefresh();
    } catch (err) {
      errorEl.textContent = `Error: ${err.message}`;
      errorEl.style.display = 'block';
      saveBtn.disabled = false;
      saveBtn.textContent = isEdit ? 'Save' : 'Add';
    }
  });

  const cancelBtn = document.createElement('button');
  cancelBtn.className = 'btn btn--secondary';
  cancelBtn.textContent = 'Cancel';
  cancelBtn.addEventListener('click', () => form.remove());

  btnRow.appendChild(saveBtn);
  btnRow.appendChild(cancelBtn);
  form.appendChild(btnRow);

  // Insert form into the modal's line items area
  const lineItemsContainer = document.getElementById('invoice-line-items');
  if (lineItemsContainer) {
    lineItemsContainer.parentNode.insertBefore(form, lineItemsContainer.nextSibling);
  }
}

/**
 * Recalculate the invoice amount_due as the sum of all line items.
 * Updates the flight record in the database (since billing is on the flights table).
 */
async function recalcInvoiceTotal(record) {
  try {
    const lines = await getBillingRecordLines(record.id);
    const total = lines.reduce((sum, l) => sum + (Number(l.amount) || 0), 0);
    await updateFlight(record.id, { amount_due: total });
    // Update the cached record so the grid reflects the change
    record.amount_due = total;
    const cached = _allBillingRecords.find((r) => r.id === record.id);
    if (cached) cached.amount_due = total;
  } catch (err) {
    console.error('Failed to recalculate invoice total:', err);
  }
}

/**
 * Render payment allocations for an invoice inside the given section.
 *
 * @param {HTMLElement} section - Container element
 * @param {Object[]} allocations - Payment allocation records
 * @param {Object} record - The billing record (legacy shape)
 */
function renderPaymentAllocations(section, allocations, record) {
  // Show remaining balance
  const invoiceAmount = Number(record.amount_due) || 0;
  const totalAllocated = (allocations || []).reduce((sum, a) => sum + (Number(a.amount) || 0), 0);
  const remaining = computeRemainingBalance(invoiceAmount, totalAllocated);

  const balanceInfo = document.createElement('div');
  balanceInfo.style.cssText = 'margin-bottom:0.75rem;font-size:0.9rem;';
  const remainingColor = remaining > 0 ? 'var(--color-danger, red)' : 'var(--color-success, green)';
  balanceInfo.innerHTML = `
    <span><strong>Total Due:</strong> ${formatGBP(invoiceAmount)}</span> ·
    <span><strong>Paid:</strong> ${formatGBP(totalAllocated)}</span> ·
    <span style="color:${remainingColor};"><strong>Remaining:</strong> ${formatGBP(remaining)}</span>
  `;
  section.appendChild(balanceInfo);

  if (!allocations || allocations.length === 0) {
    const empty = document.createElement('p');
    empty.style.color = 'var(--color-text-muted)';
    empty.textContent = 'No payments allocated to this invoice.';
    section.appendChild(empty);
    return;
  }

  const table = document.createElement('table');
  table.style.cssText = 'width:100%;border-collapse:collapse;';

  const thead = document.createElement('thead');
  thead.innerHTML = `
    <tr>
      <th style="text-align:left;padding:0.5rem;border-bottom:2px solid var(--color-border, #ddd);">Date</th>
      <th style="text-align:left;padding:0.5rem;border-bottom:2px solid var(--color-border, #ddd);">Description</th>
      <th style="text-align:right;padding:0.5rem;border-bottom:2px solid var(--color-border, #ddd);">Amount Allocated</th>
    </tr>
  `;
  table.appendChild(thead);

  const tbody = document.createElement('tbody');
  for (const alloc of allocations) {
    const entry = alloc.pilot_account_entries || {};
    const tr = document.createElement('tr');
    tr.style.borderBottom = '1px solid var(--color-border, #eee)';
    tr.innerHTML = `
      <td style="padding:0.5rem;">${entry.entry_date ? formatDateUK(entry.entry_date) : '—'}</td>
      <td style="padding:0.5rem;">${escapeHtml(entry.description || 'Payment')}</td>
      <td style="text-align:right;padding:0.5rem;color:var(--color-success, green);">${formatGBP(Number(alloc.amount) || 0)}</td>
    `;
    tbody.appendChild(tr);
  }
  table.appendChild(tbody);
  section.appendChild(table);
}

/**
 * Open a payment allocation form for a credit entry.
 * Shows outstanding invoices for the pilot and allows the admin to allocate
 * the credit amount across one or more invoices.
 *
 * @param {string} pilotId - The pilot's UUID
 * @param {number} creditAmount - The total credit entry amount
 * @param {string} accountEntryId - The account entry UUID
 * @param {Function} onComplete - Callback when allocation is complete
 */
async function openPaymentAllocationModal(pilotId, creditAmount, accountEntryId, onComplete) {
  // Remove any existing allocation modal
  const existing = document.getElementById('payment-allocation-modal');
  if (existing) existing.remove();

  const overlay = document.createElement('div');
  overlay.id = 'payment-allocation-modal';
  overlay.style.cssText = `
    position:fixed;top:0;left:0;width:100%;height:100%;
    background:rgba(0,0,0,0.5);z-index:1100;
    display:flex;align-items:center;justify-content:center;
  `;
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) closeAllocationModal();
  });

  const modal = document.createElement('div');
  modal.style.cssText = `
    background:var(--color-bg, #fff);border-radius:0.75rem;
    padding:1.5rem;max-width:750px;width:90%;max-height:85vh;
    overflow-y:auto;box-shadow:0 4px 24px rgba(0,0,0,0.2);
  `;

  // Header
  const header = document.createElement('div');
  header.style.cssText = 'display:flex;justify-content:space-between;align-items:center;margin-bottom:1rem;';

  const title = document.createElement('h3');
  title.style.margin = '0';
  title.textContent = 'Allocate Payment';

  const closeBtn = document.createElement('button');
  closeBtn.className = 'btn btn--secondary';
  closeBtn.textContent = '✕';
  closeBtn.style.cssText = 'font-size:1.25rem;padding:0.25rem 0.5rem;line-height:1;';
  closeBtn.addEventListener('click', closeAllocationModal);

  header.appendChild(title);
  header.appendChild(closeBtn);
  modal.appendChild(header);

  // Credit info
  const creditInfo = document.createElement('div');
  creditInfo.style.cssText = 'margin-bottom:1rem;font-size:1rem;';
  creditInfo.innerHTML = `<strong>Credit Amount:</strong> ${formatGBP(creditAmount)}`;
  modal.appendChild(creditInfo);

  // Remaining to allocate display
  const remainingDisplay = document.createElement('div');
  remainingDisplay.id = 'allocation-remaining';
  remainingDisplay.style.cssText = 'margin-bottom:1rem;font-size:0.95rem;font-weight:bold;';
  remainingDisplay.textContent = `Remaining to allocate: ${formatGBP(creditAmount)}`;
  modal.appendChild(remainingDisplay);

  // Loading
  const loadingEl = document.createElement('p');
  loadingEl.textContent = 'Loading outstanding invoices…';
  modal.appendChild(loadingEl);

  overlay.appendChild(modal);
  document.body.appendChild(overlay);

  // Load outstanding invoices
  let invoices = [];
  try {
    invoices = await getOutstandingInvoices(pilotId);
  } catch (err) {
    loadingEl.textContent = `Error loading invoices: ${err.message}`;
    return;
  }

  loadingEl.remove();

  if (invoices.length === 0) {
    const noInvoices = document.createElement('p');
    noInvoices.style.color = 'var(--color-text-muted)';
    noInvoices.textContent = 'No outstanding invoices for this pilot.';
    modal.appendChild(noInvoices);
    return;
  }

  // Invoice allocation table
  const table = document.createElement('table');
  table.style.cssText = 'width:100%;border-collapse:collapse;margin-bottom:1rem;';

  const thead = document.createElement('thead');
  thead.innerHTML = `
    <tr>
      <th style="text-align:left;padding:0.5rem;border-bottom:2px solid var(--color-border, #ddd);">Period</th>
      <th style="text-align:right;padding:0.5rem;border-bottom:2px solid var(--color-border, #ddd);">Total</th>
      <th style="text-align:right;padding:0.5rem;border-bottom:2px solid var(--color-border, #ddd);">Paid</th>
      <th style="text-align:right;padding:0.5rem;border-bottom:2px solid var(--color-border, #ddd);">Remaining</th>
      <th style="text-align:right;padding:0.5rem;border-bottom:2px solid var(--color-border, #ddd);">Allocate</th>
    </tr>
  `;
  table.appendChild(thead);

  const tbody = document.createElement('tbody');
  const allocationInputs = [];

  for (const inv of invoices) {
    const invRemaining = computeRemainingBalance(inv.total_amount, inv.paid_amount);
    if (invRemaining <= 0) continue; // Skip fully paid

    const tr = document.createElement('tr');
    tr.style.borderBottom = '1px solid var(--color-border, #eee)';

    const tdPeriod = document.createElement('td');
    tdPeriod.style.padding = '0.5rem';
    tdPeriod.textContent = `${formatDateUK(inv.period_start)} – ${formatDateUK(inv.period_end)}`;
    tr.appendChild(tdPeriod);

    const tdTotal = document.createElement('td');
    tdTotal.style.cssText = 'text-align:right;padding:0.5rem;';
    tdTotal.textContent = formatGBP(inv.total_amount);
    tr.appendChild(tdTotal);

    const tdPaid = document.createElement('td');
    tdPaid.style.cssText = 'text-align:right;padding:0.5rem;';
    tdPaid.textContent = formatGBP(inv.paid_amount || 0);
    tr.appendChild(tdPaid);

    const tdRemaining = document.createElement('td');
    tdRemaining.style.cssText = 'text-align:right;padding:0.5rem;';
    tdRemaining.textContent = formatGBP(invRemaining);
    tr.appendChild(tdRemaining);

    const tdAllocate = document.createElement('td');
    tdAllocate.style.cssText = 'text-align:right;padding:0.5rem;';
    const input = document.createElement('input');
    input.type = 'number';
    input.step = '0.01';
    input.min = '0';
    input.max = invRemaining.toFixed(2);
    input.value = '';
    input.placeholder = '0.00';
    input.style.cssText = 'width:100px;text-align:right;';
    input.dataset.invoiceId = inv.id;
    input.dataset.remaining = invRemaining;
    input.addEventListener('input', updateRemainingDisplay);
    tdAllocate.appendChild(input);
    tr.appendChild(tdAllocate);

    allocationInputs.push(input);
    tbody.appendChild(tr);
  }
  table.appendChild(tbody);
  modal.appendChild(table);

  // Error display
  const errorEl = document.createElement('p');
  errorEl.style.cssText = 'color:var(--color-danger, red);display:none;margin-bottom:0.5rem;';
  modal.appendChild(errorEl);

  // Submit button
  const btnRow = document.createElement('div');
  btnRow.style.cssText = 'display:flex;gap:0.5rem;';

  const submitBtn = document.createElement('button');
  submitBtn.className = 'btn btn--primary';
  submitBtn.textContent = 'Apply Allocations';
  submitBtn.addEventListener('click', handleSubmit);

  const cancelBtn = document.createElement('button');
  cancelBtn.className = 'btn btn--secondary';
  cancelBtn.textContent = 'Skip';
  cancelBtn.addEventListener('click', closeAllocationModal);

  btnRow.appendChild(submitBtn);
  btnRow.appendChild(cancelBtn);
  modal.appendChild(btnRow);

  function updateRemainingDisplay() {
    const totalAllocated = allocationInputs.reduce((sum, inp) => {
      return sum + (parseFloat(inp.value) || 0);
    }, 0);
    const remainingToAllocate = Math.max(0, creditAmount - totalAllocated);
    const el = document.getElementById('allocation-remaining');
    if (el) {
      el.textContent = `Remaining to allocate: ${formatGBP(remainingToAllocate)}`;
      el.style.color = remainingToAllocate > 0 ? 'var(--color-warning, orange)' : 'var(--color-success, green)';
    }
  }

  async function handleSubmit() {
    errorEl.style.display = 'none';

    // Collect allocations
    const allocations = [];
    let totalAllocated = 0;

    for (const input of allocationInputs) {
      const amt = parseFloat(input.value) || 0;
      if (amt <= 0) continue;

      const invoiceId = input.dataset.invoiceId;
      const invRemaining = parseFloat(input.dataset.remaining) || 0;

      const validation = validateAllocationAmount(amt, invRemaining, creditAmount, totalAllocated);
      if (!validation.valid) {
        errorEl.textContent = validation.error;
        errorEl.style.display = 'block';
        return;
      }

      allocations.push({ invoice_id: invoiceId, amount: amt, remaining: invRemaining });
      totalAllocated += amt;
    }

    if (allocations.length === 0) {
      closeAllocationModal();
      return;
    }

    // Final check: total allocated must not exceed credit amount
    if (totalAllocated > creditAmount + 0.005) {
      errorEl.textContent = `Total allocated (${formatGBP(totalAllocated)}) exceeds credit amount (${formatGBP(creditAmount)}).`;
      errorEl.style.display = 'block';
      return;
    }

    submitBtn.disabled = true;
    submitBtn.textContent = 'Applying…';

    try {
      for (const alloc of allocations) {
        // Create payment allocation record
        await createPaymentAllocation({
          account_entry_id: accountEntryId,
          invoice_id: alloc.invoice_id,
          amount: alloc.amount,
        });

        // Update invoice paid_amount
        const inv = invoices.find((i) => i.id === alloc.invoice_id);
        if (inv) {
          const newPaidAmount = (Number(inv.paid_amount) || 0) + alloc.amount;
          const newPaidRounded = Math.round(newPaidAmount * 100) / 100;
          const totalAmount = Number(inv.total_amount) || 0;

          const updates = { paid_amount: newPaidRounded };

          // Transition status based on payment coverage
          if (newPaidRounded >= totalAmount) {
            updates.status = 'PAID';
            updates.paid_at = new Date().toISOString();
          } else if (inv.status === 'DRAFT') {
            updates.status = 'ISSUED';
            updates.issued_at = new Date().toISOString();
          }

          await updateInvoice(alloc.invoice_id, updates);
          inv.paid_amount = newPaidRounded;
        }
      }

      closeAllocationModal();
      if (onComplete) onComplete();
    } catch (err) {
      errorEl.textContent = `Error applying allocations: ${err.message}`;
      errorEl.style.display = 'block';
      submitBtn.disabled = false;
      submitBtn.textContent = 'Apply Allocations';
    }
  }

  function closeAllocationModal() {
    overlay.remove();
    if (onComplete) onComplete();
  }
}

function showMsg(text, isError) {
  const el = document.getElementById('billing-msg');
  if (!el) return;
  el.textContent = text;
  el.style.color = isError ? 'var(--color-danger)' : 'var(--color-success)';
  el.style.padding = '0.75rem';
  el.style.marginBottom = '1rem';
}
